import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Button,
  Box,
  Typography,
  makeStyles,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "connections/auth/AuthProvider.js";
import {
  getAvailableInputsForModel01,
  getDimensionUniqueValues,
  generateModel01,
} from "../../viewServices/apiCalls";
import DimensionCard from "./DimensionCard";
import DimensionTable from "./DimensionTable";
import DimensionSourcePanel from "./DimensionSourcePanel";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "95%",
      maxWidth: "1200px",
      height: "85vh",
      maxHeight: "800px",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
    },
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  headerTitle: {
    fontSize: "1.25rem",
    fontWeight: "500",
    color: theme.palette.secondary.main,
  },
  dialogContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Dimension cards section (top)
  cardsSection: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  dimensionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    justifyContent: "flex-start",
  },
  dimensionCard: {
    cursor: "pointer",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "6px",
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease-in-out",
    textAlign: "center",
    minWidth: "180px",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      borderColor: (props) => props.clientColor || "#1976d2",
    },
    "&.selected": {
      borderColor: (props) => props.clientColor || "#1976d2",
      borderWidth: "2px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
  },
  dimensionCardTitle: {
    fontSize: "0.875rem",
    fontWeight: "500",
    color: grayColor[2],
    marginBottom: theme.spacing(1),
  },
  dimensionCardSubtitle: {
    fontSize: "0.7rem",
    color: grayColor[1],
    marginBottom: theme.spacing(1.5),
  },
  dimensionCardSelect: {
    width: "100%",
    "& .MuiInputBase-root": {
      fontSize: "0.75rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.75rem",
    },
  },
  // Details section (bottom)
  detailsSection: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
  },
  detailsTitle: {
    fontSize: "1rem",
    fontWeight: 500,
    color: (props) => props.clientColor || "#1976d2",
    marginBottom: theme.spacing(2),
  },
  configContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
  },
  tableInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  tableLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: grayColor[2],
    textTransform: "uppercase",
  },
  tableChip: {
    fontSize: "0.75rem",
    height: "24px",
    backgroundColor: "#f5f5f5",
    fontWeight: "500",
  },
  formControl: {
    width: "100%",
  },
  columnMapping: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  columnLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: grayColor[2],
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  ufListContainer: {
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "6px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
  },
  ufListHeader: {
    padding: theme.spacing(1.5),
    backgroundColor: "#f5f5f5",
    borderBottom: `1px solid ${grayColor[8]}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  ufListTitle: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: grayColor[2],
  },
  ufListCount: {
    fontSize: "0.75rem",
    color: (props) => props.clientColor || "#1976d2",
    fontWeight: 500,
  },
  ufListContent: {
    maxHeight: "300px",
    overflowY: "auto",
    padding: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  ufListItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    cursor: "pointer",
    borderRadius: "4px",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  ufListItemText: {
    fontSize: "0.8125rem",
    color: grayColor[2],
    marginLeft: theme.spacing(1),
  },
  ufListFooter: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${grayColor[8]}`,
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    backgroundColor: "#fafafa",
  },
  ufActionButton: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
    color: (props) => props.clientColor || "#1976d2",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  emptyStateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: theme.spacing(3),
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  emptyStateText: {
    fontSize: "0.9rem",
    color: "#999",
    textAlign: "center",
    fontStyle: "italic",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  dialogActions: {
    padding: "16px 24px",
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  cancelButton: {
    color: grayColor[2],
  },
  generateButton: {
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    "&:hover": {
      backgroundColor: (props) => props.clientColor || "#1565c0",
    },
    "&:disabled": {
      backgroundColor: grayColor[11],
      color: grayColor[4],
    },
  },
}));

const DIMENSION_CONFIG = {
  product: {
    label: "Product",
    table_id: "prod01",
    columns: {},
  },
  client: {
    label: "Channel and Client",
    table_id: "chain04",
    columns: {},
  },
  location_sellin: {
    label: "Location Sellin",
    isUserSelection: true,
    selections: [
      {
        key: "industry_location",
        label: "Industry & UF",
        column_names: ["industry_reference", "node1_location"],
        unique_rows: null, // Will be set dynamically
      },
    ],
  },
  location_sellthrough: {
    label: "Location Sellthrough",
    table_id: "chain03",
    columns: {},
  },
  location_sellout: {
    label: "Location Sellout",
    isUserSelection: true,
    selections: [
      {
        key: "node3_location",
        label: "UF",
        column_names: ["node3_location", "node3_has_st"],
        unique_rows: null, // Will be set dynamically
      },
      {
        key: "pos_reference",
        label: "POS Reference",
        column_names: ["pos_reference"],
        unique_rows: null, // Will be set dynamically
      },
    ],
  },
};

const BRAZILIAN_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

// Location-specific data
const LOCATION_SELLIN_DATA = [
  ["Extrema", "MG"],
  ["Suape", "PE"],
];
const LOCATION_SELLOUT_DATA = BRAZILIAN_STATES.map((uf) => {
  // BA, GO, RJ have node3_has_st = false, all others = true
  const hasSTException = ["BA", "GO", "RJ"];
  return [uf, !hasSTException.includes(uf)];
});
const POS_REFERENCE_OPTIONS = [["Atacarejo"], ["Total sem Atacarejo"]];

// Helper: Get unique_rows data for each selection dynamically
const getSelectionData = (dimensionKey, selectionKey) => {
  if (
    dimensionKey === "location_sellin" &&
    selectionKey === "industry_location"
  ) {
    return LOCATION_SELLIN_DATA;
  }
  if (dimensionKey === "location_sellout" && selectionKey === "pos_reference") {
    return POS_REFERENCE_OPTIONS;
  }
  if (
    dimensionKey === "location_sellout" &&
    selectionKey === "node3_location"
  ) {
    return LOCATION_SELLOUT_DATA;
  }
  return [];
};

// Helper: Cartesian product of N arrays
const cartesianProduct = (...arrays) => {
  if (arrays.length === 0) return [];
  if (arrays.length === 1) return arrays[0];

  return arrays.reduce((acc, array) => {
    const result = [];
    for (const x of acc) {
      for (const y of array) {
        const xFlat =
          Array.isArray(x) && x.length > 0 && !Array.isArray(x[0])
            ? x
            : [].concat(...(Array.isArray(x) ? x : [x]));
        const yFlat = Array.isArray(y) ? y : [y];
        result.push([...xFlat, ...yFlat]);
      }
    }
    return result;
  });
};

export default function DialogModelingAssistant({
  open,
  onClose,
  inputMetadata,
}) {
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({
    clientColor: userDetails?.implementation_color || "#1976d2",
  });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });

  // State
  const [availableData, setAvailableData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState(null);

  // Selected variants for each table (e.g., {"prod01": 2, "chain04": 1})
  const [selectedVariants, setSelectedVariants] = useState({});

  // NEW: Fetched dimensions with unique values and selected rows
  const [fetchedDimensions, setFetchedDimensions] = useState({
    product: null, // { variant_id, column_names, unique_rows, selected_rows, is_truncated, total_count }
    client: null,
    location_sellin: null, // { selected_rows: [['SP'], ['RJ'], ...] }
    location_sellthrough: null,
    location_sellout: null,
  });

  // NEW: Loading state per dimension
  const [loadingDimensions, setLoadingDimensions] = useState({
    product: false,
    client: false,
  });

  // Dimension configuration (kept for compatibility, but mostly unused now)
  const [dimensions, setDimensions] = useState({
    product: { columns: {} },
    client: { columns: {} },
    location_sellthrough: { columns: {} },
  });

  // Multi-selection state for user-selection dimensions with multiple tables
  const [multiSelections, setMultiSelections] = useState({});

  // NEW: State for inherit node3 from node2
  const [inheritNode3Location, setInheritNode3Location] = useState(false);

  // NEW: Generation mode ('combinacao' | 'mtrix')
  const [generationMode, setGenerationMode] = useState("combinacao");

  // Load available data on mount
  useEffect(() => {
    if (open) {
      loadAvailableData();
    }
  }, [open]);

  const loadAvailableData = async () => {
    setIsLoadingData(true);
    try {
      const data = await getAvailableInputsForModel01();
      setAvailableData(data);

      // Auto-select first variant for each table and auto-fill columns
      const initialVariants = {};
      const initialDimensions = { ...dimensions };

      Object.entries(DIMENSION_CONFIG).forEach(([dimensionKey, config]) => {
        const dimensionData = data[dimensionKey];
        if (dimensionData && dimensionData.variants.length > 0) {
          // Select first variant
          initialVariants[config.table_id] =
            dimensionData.variants[0].variant_id;

          // Auto-fill columns if they exist
          const availableColumnIds = dimensionData.columns.map(
            (col) => col.column_id,
          );
          const newColumns = {};
          Object.keys(config.columns).forEach((colKey) => {
            if (availableColumnIds.includes(colKey)) {
              newColumns[colKey] = colKey;
            }
          });
          initialDimensions[dimensionKey].columns = newColumns;
        }
      });

      setSelectedVariants(initialVariants);
      setDimensions(initialDimensions);

      // Initialize multiSelections for user-selection dimensions
      const initialMultiSelections = {};
      Object.entries(DIMENSION_CONFIG).forEach(([dimKey, config]) => {
        if (config.isUserSelection && config.selections) {
          initialMultiSelections[dimKey] = {};
          config.selections.forEach((selection) => {
            initialMultiSelections[dimKey][selection.key] = {
              selected_rows: [],
            };
          });
        }
      });
      setMultiSelections(initialMultiSelections);

      // Auto-select first dimension
      setSelectedDimension("product");
    } catch (error) {
      toast.error("Failed to load available data");
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleVariantChange = (tableId, variantId) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [tableId]: variantId,
    }));
  };

  // UNIFIED: Handle row toggle for multi-selections (user-selection dimensions)
  const handleToggleMultiSelection = (dimensionKey, selectionKey, row) => {
    setMultiSelections((prev) => {
      const current = prev[dimensionKey]?.[selectionKey]?.selected_rows || [];
      const isSelected = current.some(
        (r) => JSON.stringify(r) === JSON.stringify(row),
      );

      return {
        ...prev,
        [dimensionKey]: {
          ...prev[dimensionKey],
          [selectionKey]: {
            selected_rows: isSelected
              ? current.filter((r) => JSON.stringify(r) !== JSON.stringify(row))
              : [...current, row],
          },
        },
      };
    });
  };

  // UNIFIED: Handle row toggle for table-based dimensions
  const handleToggleRow = (dimensionKey, row) => {
    setFetchedDimensions((prev) => {
      const dimension = prev[dimensionKey];
      if (!dimension) return prev;

      const currentRows = dimension.selected_rows || [];
      const isSelected = currentRows.some(
        (selectedRow) => JSON.stringify(selectedRow) === JSON.stringify(row),
      );

      const newRows = isSelected
        ? currentRows.filter(
            (selectedRow) =>
              JSON.stringify(selectedRow) !== JSON.stringify(row),
          )
        : [...currentRows, row];

      return {
        ...prev,
        [dimensionKey]: {
          ...dimension,
          selected_rows: newRows,
        },
      };
    });
  };

  // Multi-selection handlers
  const handleSelectAllMulti = (dimensionKey, selectionKey) => {
    const availableRows = getSelectionData(dimensionKey, selectionKey);
    setMultiSelections((prev) => ({
      ...prev,
      [dimensionKey]: {
        ...prev[dimensionKey],
        [selectionKey]: {
          selected_rows: availableRows,
        },
      },
    }));
  };

  const handleClearMulti = (dimensionKey, selectionKey) => {
    setMultiSelections((prev) => ({
      ...prev,
      [dimensionKey]: {
        ...prev[dimensionKey],
        [selectionKey]: {
          selected_rows: [],
        },
      },
    }));
  };

  const handleInvertMulti = (dimensionKey, selectionKey) => {
    const availableRows = getSelectionData(dimensionKey, selectionKey);
    setMultiSelections((prev) => {
      const currentRows =
        prev[dimensionKey]?.[selectionKey]?.selected_rows || [];
      const invertedRows = availableRows.filter(
        (row) =>
          !currentRows.some((r) => JSON.stringify(r) === JSON.stringify(row)),
      );
      return {
        ...prev,
        [dimensionKey]: {
          ...prev[dimensionKey],
          [selectionKey]: {
            selected_rows: invertedRows,
          },
        },
      };
    });
  };

  // UNIFIED: Handle "Select All" (for table-based dimensions)
  const handleSelectAll = (dimensionKey) => {
    setFetchedDimensions((prev) => {
      const dimension = prev[dimensionKey];
      if (!dimension) return prev;

      return {
        ...prev,
        [dimensionKey]: {
          ...dimension,
          selected_rows: dimension.unique_rows,
        },
      };
    });
  };

  // UNIFIED: Handle "Clear" (for table-based dimensions)
  const handleClear = (dimensionKey) => {
    setFetchedDimensions((prev) => {
      const dimension = prev[dimensionKey];
      if (!dimension) return prev;

      return {
        ...prev,
        [dimensionKey]: {
          ...dimension,
          selected_rows: [],
        },
      };
    });
  };

  // UNIFIED: Handle "Invert" (for table-based dimensions)
  const handleInvert = (dimensionKey) => {
    setFetchedDimensions((prev) => {
      const dimension = prev[dimensionKey];
      if (!dimension) return prev;

      const currentRows = dimension.selected_rows;
      const invertedRows = dimension.unique_rows.filter(
        (row) =>
          !currentRows.some(
            (selectedRow) =>
              JSON.stringify(selectedRow) === JSON.stringify(row),
          ),
      );

      return {
        ...prev,
        [dimensionKey]: {
          ...dimension,
          selected_rows: invertedRows,
        },
      };
    });
  };

  // NEW: Handle "Use" button - fetch unique values for dimension
  const handleUseDimension = async (dimensionKey) => {
    const config = DIMENSION_CONFIG[dimensionKey];
    const variantId = selectedVariants[config.table_id];

    if (!variantId) {
      toast.warning("Select a variant first");
      return;
    }

    setLoadingDimensions((prev) => ({ ...prev, [dimensionKey]: true }));

    try {
      const data = await getDimensionUniqueValues(dimensionKey, variantId);

      setFetchedDimensions((prev) => ({
        ...prev,
        [dimensionKey]: {
          variant_id: variantId,
          column_names: data.column_names,
          unique_rows: data.unique_rows,
          selected_rows: [], // None selected initially
          is_truncated: data.is_truncated,
          total_count: data.total_count,
        },
      }));

      if (data.is_truncated) {
        toast.info(`Showing first 500 of ${data.total_count} unique values`, {
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(`Failed to load ${dimensionKey} values`);
    } finally {
      setLoadingDimensions((prev) => ({ ...prev, [dimensionKey]: false }));
    }
  };

  // NEW: Calculate total combinations
  const getTotalCombinations = () => {
    return [
      "product",
      "client",
      "location_sellin",
      "location_sellthrough",
      "location_sellout",
    ].reduce((total, dim) => {
      const config = DIMENSION_CONFIG[dim];
      let count = 0;

      if (config.isUserSelection && config.selections) {
        // Multi-selection: product of all selection counts
        count = config.selections.reduce((prod, sel) => {
          const selCount =
            multiSelections[dim]?.[sel.key]?.selected_rows?.length || 0;

          // Special case: node3_location with inherit enabled counts as 1
          if (
            dim === "location_sellout" &&
            sel.key === "node3_location" &&
            inheritNode3Location
          ) {
            return prod * 1;
          }

          return prod * selCount;
        }, 1);
      } else {
        // Table-based
        count = fetchedDimensions[dim]?.selected_rows?.length || 0;
      }

      return total === 0 ? count : total * count;
    }, 0);
  };

  // NEW: Validate if can generate
  const canGenerate = () => {
    // TODO: Implement specific validation for Mtrix mode
    if (generationMode === "mtrix") {
      return false;
    }

    return [
      "product",
      "client",
      "location_sellin",
      "location_sellthrough",
      "location_sellout",
    ].every((dim) => {
      const config = DIMENSION_CONFIG[dim];

      if (config.isUserSelection && config.selections) {
        // All selections must have at least 1 row
        return config.selections.every((sel) => {
          // Special case: node3_location with inherit enabled doesn't need validation
          if (
            dim === "location_sellout" &&
            sel.key === "node3_location" &&
            inheritNode3Location
          ) {
            return true;
          }

          const selCount =
            multiSelections[dim]?.[sel.key]?.selected_rows?.length || 0;
          return selCount > 0;
        });
      } else {
        // Table-based
        const data = fetchedDimensions[dim];
        return data && data.selected_rows && data.selected_rows.length > 0;
      }
    });
  };

  const handleGenerate = async () => {
    // Validate combinations
    const totalCombinations = getTotalCombinations();
    const MAX_ROWS = 1_000_000;

    if (totalCombinations > MAX_ROWS) {
      toast.error(
        `${totalCombinations.toLocaleString()} combinations exceed limit of ${MAX_ROWS.toLocaleString()} rows`,
        { autoClose: 8000 },
      );
      return;
    }

    setIsGenerating(true);
    try {
      // Create cartesian product of locations
      // For location_sellin: get combinations from multi-selections
      const getLocationCombinations = (dimensionKey) => {
        const config = DIMENSION_CONFIG[dimensionKey];
        if (config.isUserSelection && config.selections) {
          // Get selected_rows from each selection
          const selectedArrays = config.selections.map(
            (sel) =>
              multiSelections[dimensionKey]?.[sel.key]?.selected_rows || [],
          );

          // If any selection is empty, return empty
          if (selectedArrays.some((arr) => arr.length === 0)) {
            return [];
          }

          // If only 1 selection, return as is
          if (selectedArrays.length === 1) {
            return selectedArrays[0];
          }

          // Multiple selections: cartesian product
          return cartesianProduct(...selectedArrays);
        } else {
          // Table-based dimension
          return fetchedDimensions[dimensionKey]?.selected_rows || [];
        }
      };

      // Get combinations for each location dimension (FULL ROWS, not just UFs)
      const sellinCombinations = getLocationCombinations("location_sellin");
      const sellthroughCombinations = getLocationCombinations(
        "location_sellthrough",
      );

      // Handle location_sellout with special case for inheritance
      let selloutCombinations;
      let selloutColumnNames;

      if (inheritNode3Location) {
        // When inheriting node3_location, only send pos_reference
        const posReferenceRows =
          multiSelections["location_sellout"]?.["pos_reference"]
            ?.selected_rows || [];

        if (posReferenceRows.length === 0) {
          toast.error("Selecione pelo menos um POS Reference");
          return;
        }

        selloutCombinations = posReferenceRows; // Just pos_reference rows
        selloutColumnNames = ["pos_reference"]; // Only pos_reference column
      } else {
        // Normal behavior: get full combinations from both selections
        selloutCombinations = getLocationCombinations("location_sellout");
        selloutColumnNames =
          DIMENSION_CONFIG.location_sellout.selections.flatMap(
            (s) => s.column_names,
          );
      }

      // Prepare payload with 5 separate dimensions (generic architecture)
      const dimensions = [];

      // 1. Product
      dimensions.push({
        dimension_key: "product",
        variant_id: fetchedDimensions.product.variant_id,
        column_names: fetchedDimensions.product.column_names,
        selected_rows: fetchedDimensions.product.selected_rows,
      });

      // 2. Client
      dimensions.push({
        dimension_key: "client",
        variant_id: fetchedDimensions.client.variant_id,
        column_names: fetchedDimensions.client.column_names,
        selected_rows: fetchedDimensions.client.selected_rows,
      });

      // 3. Location Sellin
      dimensions.push({
        dimension_key: "location_sellin",
        column_names:
          DIMENSION_CONFIG.location_sellin.selections[0].column_names,
        selected_rows: sellinCombinations,
      });

      // 4. Location Sellthrough
      dimensions.push({
        dimension_key: "location_sellthrough",
        variant_id: fetchedDimensions.location_sellthrough?.variant_id,
        column_names: fetchedDimensions.location_sellthrough.column_names,
        selected_rows: sellthroughCombinations,
      });

      // 5. Location Sellout
      dimensions.push({
        dimension_key: "location_sellout",
        column_names: selloutColumnNames,
        selected_rows: selloutCombinations,
        metadata: inheritNode3Location
          ? {
              inherit_columns: [
                {
                  source_dimension: "location_sellthrough",
                  source_column: "region_uf",
                  target_column: "node3_location",
                },
                {
                  source_dimension: "location_sellthrough",
                  source_column: "region_has_st",
                  target_column: "node3_has_st",
                },
              ],
            }
          : { inherit_columns: [] },
      });

      const payload = { dimensions };

      const blob = await generateModel01(payload);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "model01_generated.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Model01 gerado com sucesso!");
      onClose();
    } catch (error) {
      console.error("Generation error:", error);

      // Extract error message from backend
      let errorMessage = "Erro ao gerar model01";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        autoClose: 8000,
        style: { whiteSpace: "pre-line" },
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getAvailableVariants = (dimensionKey) => {
    if (!availableData) return [];
    const dimensionData = availableData[dimensionKey];
    return dimensionData ? dimensionData.variants : [];
  };

  const renderDimensionDetails = () => {
    // Placeholder for Mtrix mode
    if (generationMode === "mtrix") {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          style={{
            backgroundColor: "#fafafa",
            borderRadius: "8px",
            padding: "40px",
          }}
        >
          <Typography
            style={{
              fontSize: "1rem",
              color: "#999",
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Configuração via Mtrix selecionada.
            <br />
            As dimensões serão definidas a partir da matriz existente.
          </Typography>
        </Box>
      );
    }

    if (!selectedDimension) {
      return (
        <Box className={classes.emptyStateContainer}>
          <Typography className={classes.emptyStateText}>
            Click on a dimension card to configure
          </Typography>
        </Box>
      );
    }

    const config = DIMENSION_CONFIG[selectedDimension];
    const isUserSelection = config.isUserSelection;

    // Special rendering for user-selection dimensions (with multiple tables)
    if (isUserSelection) {
      const selections = config.selections || [];

      return (
        <>
          <Typography
            className={classes.detailsTitle}
            style={{ flexShrink: 0 }}
          >
            {config.label} Configuration
          </Typography>

          <Box
            className={classes.configContainer}
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box display="flex" gap={4} style={{ flex: 1, minHeight: 0 }}>
              <DimensionSourcePanel
                isUserSelection={true}
                clientColor={userDetails?.implementation_color}
                showInheritToggle={selectedDimension === "location_sellout"}
                inheritFromNode2={inheritNode3Location}
                onInheritToggle={setInheritNode3Location}
              />

              {/* Render N tables side-by-side dynamically */}
              <Box
                display="flex"
                gap={3}
                style={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  paddingTop: 28,
                  marginTop: -28,
                  overflowX: "auto",
                }}
              >
                {selections.map((selection) => {
                  const selectionData = multiSelections[selectedDimension]?.[
                    selection.key
                  ] || { selected_rows: [] };
                  const uniqueRows = getSelectionData(
                    selectedDimension,
                    selection.key,
                  );

                  const dimension = {
                    column_names: selection.column_names,
                    unique_rows: uniqueRows,
                    selected_rows: selectionData.selected_rows,
                  };

                  // Check if this selection should be disabled (node3_location with inherit)
                  const isDisabled =
                    selectedDimension === "location_sellout" &&
                    selection.key === "node3_location" &&
                    inheritNode3Location;

                  return (
                    <Box
                      key={selection.key}
                      display="flex"
                      flexDirection="column"
                      style={{
                        flex: "0 0 auto",
                        minHeight: 0,
                        position: "relative",
                      }}
                    >
                      {/* Floating card message when inheriting node3_location */}
                      {isDisabled && (
                        <Box
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 10,
                            backgroundColor: "#fff",
                            border: "2px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "16px 24px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            pointerEvents: "none",
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: "#666",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{ fontSize: "1.2em", color: "#4caf50" }}
                            >
                              ✓
                            </span>
                            Valores herdados de Sellthrough
                          </Typography>
                        </Box>
                      )}
                      <DimensionTable
                        dimension={dimension}
                        onRowToggle={(row) =>
                          handleToggleMultiSelection(
                            selectedDimension,
                            selection.key,
                            row,
                          )
                        }
                        onSelectAll={() =>
                          handleSelectAllMulti(selectedDimension, selection.key)
                        }
                        onClear={() =>
                          handleClearMulti(selectedDimension, selection.key)
                        }
                        onInvert={() =>
                          handleInvertMulti(selectedDimension, selection.key)
                        }
                        disabled={isDisabled}
                        clientColor={userDetails?.implementation_color}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </>
      );
    }

    // Regular table-based dimensions
    const tableId = config.table_id;
    const tableName = inputMetadata[tableId]?.table_description || tableId;
    const variants = getAvailableVariants(selectedDimension);
    const selectedVariantId = selectedVariants[tableId];
    const dimension = fetchedDimensions[selectedDimension];
    const isLoading = loadingDimensions[selectedDimension];

    return (
      <>
        <Typography className={classes.detailsTitle} style={{ flexShrink: 0 }}>
          {config.label} Configuration
        </Typography>

        <Box
          className={classes.configContainer}
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            gap={4}
            style={{
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "visible",
            }}
          >
            <DimensionSourcePanel
              isUserSelection={false}
              tableName={tableName}
              tableId={tableId}
              variants={variants}
              selectedVariantId={selectedVariantId}
              onVariantChange={(variantId) =>
                handleVariantChange(tableId, variantId)
              }
              onUse={() => handleUseDimension(selectedDimension)}
              isLoading={isLoading}
              clientColor={userDetails?.implementation_color}
            />

            {dimension && dimension.unique_rows.length > 0 && (
              <DimensionTable
                dimension={dimension}
                onRowToggle={(row) => handleToggleRow(selectedDimension, row)}
                onSelectAll={() => handleSelectAll(selectedDimension)}
                onClear={() => handleClear(selectedDimension)}
                onInvert={() => handleInvert(selectedDimension)}
                isLoading={isLoading}
                clientColor={userDetails?.implementation_color}
              />
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className={classes.dialog}
    >
      {/* Header */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.headerTitle}>
          {t("modeling_assistant")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box className={classes.dialogContent}>
        {isLoadingData ? (
          <Box className={classes.loadingContainer}>
            <ThreeDots color={userDetails?.implementation_color || "#1976d2"} />
            <Typography>Loading configuration...</Typography>
          </Box>
        ) : (
          <>
            {/* Dimension Cards Section */}
            <Box className={classes.cardsSection}>
              <Box display="flex" alignItems="flex-start">
                {/* Generation Mode Dropdown */}
                <Box
                  display="flex"
                  flexDirection="column"
                  style={{ minWidth: 150, paddingTop: 8 }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>Modo de geração</InputLabel>
                    <Select
                      value={generationMode}
                      onChange={(e) => setGenerationMode(e.target.value)}
                    >
                      <MenuItem value="combinacao">Combinação</MenuItem>
                      <MenuItem value="mtrix">Mtrix</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Vertical Divider */}
                <Box width="1px" height="95px" bgcolor="#d0d0d0" marginX={3} />

                {/* Dimension Cards */}
                <Box
                  className={classes.dimensionsContainer}
                  style={{ flex: 1 }}
                >
                  {Object.entries(DIMENSION_CONFIG).map(
                    ([dimensionKey, config]) => {
                      let selectionCount = 0;
                      let selectionLabel = null;

                      if (config.isUserSelection && config.selections) {
                        // Multi-selection: product of all selection counts
                        const counts = config.selections.map((sel) => {
                          // Special case: node3_location with inherit counts as 1
                          if (
                            dimensionKey === "location_sellout" &&
                            sel.key === "node3_location" &&
                            inheritNode3Location
                          ) {
                            return 1;
                          }
                          return (
                            multiSelections[dimensionKey]?.[sel.key]
                              ?.selected_rows?.length || 0
                          );
                        });
                        selectionCount = counts.reduce(
                          (prod, count) => prod * count,
                          1,
                        );
                        selectionLabel = `${counts.join(" x ")} selected`;
                      } else {
                        // Table-based
                        const dimension = fetchedDimensions[dimensionKey];
                        selectionCount = dimension?.selected_rows?.length || 0;
                      }

                      return (
                        <DimensionCard
                          key={dimensionKey}
                          label={config.label}
                          selectionCount={selectionCount}
                          selectionLabel={selectionLabel}
                          isSelected={selectedDimension === dimensionKey}
                          onClick={() => setSelectedDimension(dimensionKey)}
                          clientColor={userDetails?.implementation_color}
                          disabled={generationMode === "mtrix"}
                        />
                      );
                    },
                  )}
                </Box>
              </Box>
            </Box>

            {/* Details Section */}
            <Box className={classes.detailsSection}>
              {renderDimensionDetails()}
            </Box>
          </>
        )}
      </Box>

      {/* Actions */}
      <Box className={classes.dialogActions}>
        {/* Preview */}
        <Box flex={1}>
          {canGenerate() && generationMode !== "mtrix" && (
            <Typography variant="caption" style={{ color: "#666" }}>
              Preview:{" "}
              {[
                "product",
                "client",
                "location_sellin",
                "location_sellthrough",
                "location_sellout",
              ]
                .map((dim) => {
                  const config = DIMENSION_CONFIG[dim];

                  if (config.isUserSelection && config.selections) {
                    // Multi-selection: show as (2 x 3) for clarity
                    const counts = config.selections.map((sel) => {
                      // Special case: node3_location with inherit counts as 1
                      if (
                        dim === "location_sellout" &&
                        sel.key === "node3_location" &&
                        inheritNode3Location
                      ) {
                        return 1;
                      }
                      return (
                        multiSelections[dim]?.[sel.key]?.selected_rows
                          ?.length || 0
                      );
                    });
                    return `(${counts.join(" x ")})`;
                  } else {
                    // Table-based
                    return fetchedDimensions[dim]?.selected_rows?.length || 0;
                  }
                })
                .join(" × ")}{" "}
              = <strong>{getTotalCombinations().toLocaleString()} rows</strong>
            </Typography>
          )}
        </Box>

        <Button
          onClick={onClose}
          disabled={isGenerating}
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          className={classes.generateButton}
          disabled={!canGenerate() || isGenerating}
          startIcon={
            isGenerating && <CircularProgress size={20} color="inherit" />
          }
        >
          {isGenerating ? "Generating..." : "Generate & Download"}
        </Button>
      </Box>
    </Dialog>
  );
}

DialogModelingAssistant.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inputMetadata: PropTypes.object.isRequired,
};
